import { getToken, clearToken } from '../utils/authToken';
import { useState, useEffect } from 'react';
import { API_URL, API } from '../config';
import { createPortal } from 'react-dom';
import { 
  Brain, User, Code, Sparkles, Save, X, Trash2, Plus, Loader2, 
  Search, Filter, Edit2, Check, XCircle, Tag, AlertTriangle,
  Settings2, Briefcase, Heart, Wrench, FileText, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';


// Configuración de categorías con iconos y colores
const CATEGORY_CONFIG = {
  preference: { 
    icon: Heart, 
    color: 'text-pink-400', 
    bg: 'bg-pink-500/20',
    label: { es: 'Preferencia', en: 'Preference' }
  },
  project: { 
    icon: Briefcase, 
    color: 'text-blue-400', 
    bg: 'bg-blue-500/20',
    label: { es: 'Proyecto', en: 'Project' }
  },
  configuration: { 
    icon: Settings2, 
    color: 'text-orange-400', 
    bg: 'bg-orange-500/20',
    label: { es: 'Configuración', en: 'Configuration' }
  },
  personal: { 
    icon: User, 
    color: 'text-green-400', 
    bg: 'bg-green-500/20',
    label: { es: 'Personal', en: 'Personal' }
  },
  technical: { 
    icon: Wrench, 
    color: 'text-cyan-400', 
    bg: 'bg-cyan-500/20',
    label: { es: 'Técnico', en: 'Technical' }
  },
  context: { 
    icon: FileText, 
    color: 'text-violet-400', 
    bg: 'bg-violet-500/20',
    label: { es: 'Contexto', en: 'Context' }
  }
};

// Configuración de prioridades
const PRIORITY_CONFIG = {
  low: { color: 'text-zinc-400', bg: 'bg-zinc-500/20', label: { es: 'Baja', en: 'Low' } },
  medium: { color: 'text-yellow-400', bg: 'bg-yellow-500/20', label: { es: 'Media', en: 'Medium' } },
  high: { color: 'text-orange-400', bg: 'bg-orange-500/20', label: { es: 'Alta', en: 'High' } },
  critical: { color: 'text-red-400', bg: 'bg-red-500/20', label: { es: 'Crítica', en: 'Critical' } }
};

const MemoryProfileModal = ({ isOpen, onClose }) => {
  const { t, language } = useLanguage();
  
  const EXPERTISE_LEVELS = [
    { value: 'beginner', label: language === 'es' ? 'Principiante' : 'Beginner', icon: '🌱' },
    { value: 'intermediate', label: language === 'es' ? 'Intermedio' : 'Intermediate', icon: '🌿' },
    { value: 'advanced', label: language === 'es' ? 'Avanzado' : 'Advanced', icon: '🌳' },
  ];

  const CODING_STYLES = [
    { value: 'concise', label: language === 'es' ? 'Conciso - Código directo al punto' : 'Concise - Straight to the point code' },
    { value: 'verbose', label: language === 'es' ? 'Detallado - Con explicaciones extensas' : 'Verbose - With extensive explanations' },
    { value: 'documented', label: language === 'es' ? 'Documentado - Con comentarios completos' : 'Documented - With full comments' },
  ];

  const POPULAR_STACKS = [
    'React', 'Vue', 'Angular', 'Next.js', 'Node.js', 'Python', 'FastAPI', 
    'Django', 'MongoDB', 'PostgreSQL', 'TypeScript', 'TailwindCSS', 'Docker'
  ];

  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState({
    display_name: '',
    preferred_language: 'español',
    preferred_stack: [],
    coding_style: 'documented',
    expertise_level: 'intermediate',
    interests: [],
    custom_instructions: ''
  });
  const [memories, setMemories] = useState([]);
  const [newMemory, setNewMemory] = useState('');
  const [newCategory, setNewCategory] = useState('context');
  const [newPriority, setNewPriority] = useState('medium');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Filtros y búsqueda
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Edición
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editPriority, setEditPriority] = useState('');
  
  // Confirmación de eliminación
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      if (!token || token === 'guest') {
        toast.error(language === 'es' ? 'Inicia sesión para usar esta función' : 'Sign in to use this feature');
        onClose();
        return;
      }

      const headers = {};  // P2.1 cookie-only via axios.defaults
      
      const [profileRes, memoriesRes] = await Promise.all([
        axios.get(`${API}/profile`, { headers }),
        axios.get(`${API}/memory`, { headers })
      ]);

      setProfile(prev => ({ ...prev, ...profileRes.data }));
      setMemories(memoriesRes.data.memories || []);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) {
        toast.error(language === 'es' ? 'Sesión expirada. Inicia sesión nuevamente.' : 'Session expired. Please sign in again.');
        onClose();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const saveProfile = async () => {
    setIsSaving(true);
    try {
      const token = getToken();
      await axios.put(`${API}/profile`, profile, {
      });
      toast.success(language === 'es' ? 'Perfil guardado' : 'Profile saved');
    } catch (error) {
      toast.error(language === 'es' ? 'Error al guardar perfil' : 'Error saving profile');
    } finally {
      setIsSaving(false);
    }
  };

  const addMemory = async () => {
    if (!newMemory.trim()) return;
    try {
      const token = getToken();
      const res = await axios.post(`${API}/memory`, 
        { 
          content: newMemory, 
          category: newCategory,
          priority: newPriority
        },
        {}
      );
      setMemories([res.data.memory, ...memories]);
      setNewMemory('');
      setNewCategory('context');
      setNewPriority('medium');
      toast.success(language === 'es' ? 'Memoria añadida' : 'Memory added');
    } catch (error) {
      toast.error(language === 'es' ? 'Error al añadir memoria' : 'Error adding memory');
    }
  };

  const updateMemory = async (memoryId) => {
    try {
      const token = getToken();
      await axios.put(`${API}/memory/${memoryId}`, 
        { 
          content: editContent,
          category: editCategory,
          priority: editPriority
        },
        {}
      );
      setMemories(memories.map(m => 
        m.id === memoryId 
          ? { ...m, content: editContent, category: editCategory, priority: editPriority }
          : m
      ));
      setEditingId(null);
      toast.success(language === 'es' ? 'Memoria actualizada' : 'Memory updated');
    } catch (error) {
      toast.error(language === 'es' ? 'Error al actualizar' : 'Error updating');
    }
  };

  const deleteMemory = async (memoryId) => {
    try {
      const token = getToken();
      await axios.delete(`${API}/memory/${memoryId}`, {
      });
      setMemories(memories.filter(m => m.id !== memoryId));
      setDeleteConfirm(null);
      toast.success(language === 'es' ? 'Memoria eliminada' : 'Memory deleted');
    } catch (error) {
      toast.error(language === 'es' ? 'Error al eliminar' : 'Error deleting');
    }
  };

  const clearAllMemories = async () => {
    try {
      const token = getToken();
      await axios.delete(`${API}/memory/clear/all`, {
      });
      setMemories([]);
      setDeleteConfirm(null);
      toast.success(language === 'es' ? 'Todas las memorias eliminadas' : 'All memories deleted');
    } catch (error) {
      toast.error(language === 'es' ? 'Error al limpiar memorias' : 'Error clearing memories');
    }
  };

  const startEditing = (memory) => {
    setEditingId(memory.id);
    setEditContent(memory.content);
    setEditCategory(memory.category || 'context');
    setEditPriority(memory.priority || 'medium');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditContent('');
    setEditCategory('');
    setEditPriority('');
  };

  const toggleStack = (tech) => {
    setProfile(prev => ({
      ...prev,
      preferred_stack: prev.preferred_stack.includes(tech)
        ? prev.preferred_stack.filter(t => t !== tech)
        : [...prev.preferred_stack, tech]
    }));
  };

  // Filtrar memorias
  const filteredMemories = memories.filter(memory => {
    const matchesSearch = searchQuery === '' || 
      memory.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || 
      memory.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Obtener conteo por categoría
  const categoryCounts = memories.reduce((acc, m) => {
    const cat = m.category || 'context';
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});

  if (!isOpen) return null;

  const CategoryIcon = ({ category, size = 'w-4 h-4' }) => {
    const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.context;
    const Icon = config.icon;
    return <Icon className={`${size} ${config.color}`} />;
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-3xl bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-violet-600 to-purple-600 rounded-lg">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-white">{t('memory.title')}</h2>
              <p className="text-xs text-zinc-400">
                {language === 'es' ? 'Gestiona tus memorias persistentes (MCC)' : 'Manage your persistent memories (MCC)'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg" data-testid="close-memory-modal">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-700">
          <button
            onClick={() => setActiveTab('profile')}
            data-testid="tab-profile"
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'profile' 
                ? 'text-violet-400 border-b-2 border-violet-400 bg-violet-500/10' 
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            <User className="w-4 h-4 inline mr-2" />
            {language === 'es' ? 'Perfil' : 'Profile'}
          </button>
          <button
            onClick={() => setActiveTab('memory')}
            data-testid="tab-memories"
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'memory' 
                ? 'text-violet-400 border-b-2 border-violet-400 bg-violet-500/10' 
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Brain className="w-4 h-4 inline mr-2" />
            {language === 'es' ? 'Memorias' : 'Memories'} ({memories.length})
          </button>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 160px)' }}>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
            </div>
          ) : activeTab === 'profile' ? (
            <div className="space-y-5">
              {/* Display Name */}
              <div>
                <label className="block text-sm text-zinc-300 mb-2">
                  {language === 'es' ? 'Tu nombre' : 'Your name'}
                </label>
                <input
                  type="text"
                  value={profile.display_name || ''}
                  onChange={(e) => setProfile(p => ({ ...p, display_name: e.target.value }))}
                  placeholder={language === 'es' ? '¿Cómo quieres que te llame?' : 'What should I call you?'}
                  className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-600 rounded-lg text-white placeholder-zinc-500 focus:ring-2 focus:ring-violet-500"
                  data-testid="input-display-name"
                />
              </div>

              {/* Expertise Level */}
              <div>
                <label className="block text-sm text-zinc-300 mb-2">
                  {language === 'es' ? 'Nivel de experiencia' : 'Experience level'}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {EXPERTISE_LEVELS.map(level => (
                    <button
                      key={level.value}
                      onClick={() => setProfile(p => ({ ...p, expertise_level: level.value }))}
                      data-testid={`expertise-${level.value}`}
                      className={`p-3 rounded-lg border text-sm transition-all ${
                        profile.expertise_level === level.value
                          ? 'bg-violet-600 border-violet-500 text-white'
                          : 'bg-zinc-800 border-zinc-600 text-zinc-300 hover:border-zinc-500'
                      }`}
                    >
                      <span className="text-lg">{level.icon}</span>
                      <span className="ml-2">{level.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Preferred Stack */}
              <div>
                <label className="block text-sm text-zinc-300 mb-2">
                  <Code className="w-4 h-4 inline mr-1" />
                  {language === 'es' ? 'Tecnologías preferidas' : 'Preferred technologies'}
                </label>
                <div className="flex flex-wrap gap-2">
                  {POPULAR_STACKS.map(tech => (
                    <button
                      key={tech}
                      onClick={() => toggleStack(tech)}
                      data-testid={`stack-${tech.toLowerCase()}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        profile.preferred_stack?.includes(tech)
                          ? 'bg-violet-600 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {tech}
                    </button>
                  ))}
                </div>
              </div>

              {/* Coding Style */}
              <div>
                <label className="block text-sm text-zinc-300 mb-2">
                  {language === 'es' ? 'Estilo de respuestas' : 'Response style'}
                </label>
                <select
                  value={profile.coding_style || 'documented'}
                  onChange={(e) => setProfile(p => ({ ...p, coding_style: e.target.value }))}
                  data-testid="select-coding-style"
                  className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-violet-500"
                >
                  {CODING_STYLES.map(style => (
                    <option key={style.value} value={style.value}>{style.label}</option>
                  ))}
                </select>
              </div>

              {/* Custom Instructions */}
              <div>
                <label className="block text-sm text-zinc-300 mb-2">
                  <Sparkles className="w-4 h-4 inline mr-1" />
                  {language === 'es' ? 'Instrucciones personalizadas' : 'Custom instructions'}
                </label>
                <textarea
                  value={profile.custom_instructions || ''}
                  onChange={(e) => setProfile(p => ({ ...p, custom_instructions: e.target.value }))}
                  placeholder={language === 'es' 
                    ? 'Ej: Siempre usa TypeScript, prefiero ejemplos prácticos...' 
                    : 'E.g.: Always use TypeScript, I prefer practical examples...'}
                  rows={3}
                  data-testid="textarea-instructions"
                  className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-600 rounded-lg text-white placeholder-zinc-500 focus:ring-2 focus:ring-violet-500 resize-none"
                />
              </div>

              {/* Save Button */}
              <button
                onClick={saveProfile}
                disabled={isSaving}
                data-testid="btn-save-profile"
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-medium rounded-lg transition-all disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                {t('memory.save')} {language === 'es' ? 'Perfil' : 'Profile'}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Search and Filters */}
              <div className="space-y-3">
                {/* Search Bar */}
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={language === 'es' ? 'Buscar memorias...' : 'Search memories...'}
                      data-testid="search-memories"
                      className="w-full pl-10 pr-4 py-2.5 bg-zinc-800 border border-zinc-600 rounded-lg text-white placeholder-zinc-500 focus:ring-2 focus:ring-violet-500"
                    />
                  </div>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    data-testid="btn-toggle-filters"
                    className={`px-3 py-2.5 rounded-lg border transition-all ${
                      showFilters || filterCategory !== 'all'
                        ? 'bg-violet-600 border-violet-500 text-white'
                        : 'bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-zinc-500'
                    }`}
                  >
                    <Filter className="w-5 h-5" />
                  </button>
                </div>

                {/* Category Filters */}
                {showFilters && (
                  <div className="flex flex-wrap gap-2 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                    <button
                      onClick={() => setFilterCategory('all')}
                      data-testid="filter-all"
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        filterCategory === 'all'
                          ? 'bg-violet-600 text-white'
                          : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                      }`}
                    >
                      {language === 'es' ? 'Todas' : 'All'} ({memories.length})
                    </button>
                    {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                      <button
                        key={key}
                        onClick={() => setFilterCategory(key)}
                        data-testid={`filter-${key}`}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          filterCategory === key
                            ? `${config.bg} ${config.color}`
                            : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                        }`}
                      >
                        <CategoryIcon category={key} size="w-3 h-3" />
                        {config.label[language]} ({categoryCounts[key] || 0})
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Add Memory */}
              <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700 space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMemory}
                    onChange={(e) => setNewMemory(e.target.value)}
                    placeholder={language === 'es' 
                      ? 'Añadir nueva memoria...' 
                      : 'Add new memory...'}
                    data-testid="input-new-memory"
                    className="flex-1 px-4 py-2.5 bg-zinc-900 border border-zinc-600 rounded-lg text-white placeholder-zinc-500 focus:ring-2 focus:ring-violet-500"
                    onKeyDown={(e) => e.key === 'Enter' && addMemory()}
                  />
                  <button
                    onClick={addMemory}
                    disabled={!newMemory.trim()}
                    data-testid="btn-add-memory"
                    className="px-4 py-2.5 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:hover:bg-violet-600 text-white rounded-lg transition-all"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Category and Priority selectors */}
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-xs text-zinc-500 mb-1">
                      {language === 'es' ? 'Categoría' : 'Category'}
                    </label>
                    <select
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      data-testid="select-new-category"
                      className="w-full px-3 py-2 bg-zinc-900 border border-zinc-600 rounded-lg text-sm text-white focus:ring-2 focus:ring-violet-500"
                    >
                      {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                        <option key={key} value={key}>{config.label[language]}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-zinc-500 mb-1">
                      {language === 'es' ? 'Prioridad' : 'Priority'}
                    </label>
                    <select
                      value={newPriority}
                      onChange={(e) => setNewPriority(e.target.value)}
                      data-testid="select-new-priority"
                      className="w-full px-3 py-2 bg-zinc-900 border border-zinc-600 rounded-lg text-sm text-white focus:ring-2 focus:ring-violet-500"
                    >
                      {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                        <option key={key} value={key}>{config.label[language]}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Memories List */}
              <div className="space-y-2">
                {filteredMemories.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <Brain className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{searchQuery || filterCategory !== 'all' 
                      ? (language === 'es' ? 'No se encontraron memorias' : 'No memories found')
                      : (language === 'es' ? 'No hay memorias guardadas' : 'No saved memories')
                    }</p>
                    <p className="text-xs mt-1">
                      {language === 'es' 
                        ? 'Usa "recordá esto" en el chat para guardar memorias automáticamente' 
                        : 'Use "remember this" in chat to save memories automatically'}
                    </p>
                  </div>
                ) : (
                  filteredMemories.map(memory => {
                    const categoryConfig = CATEGORY_CONFIG[memory.category] || CATEGORY_CONFIG.context;
                    const priorityConfig = PRIORITY_CONFIG[memory.priority] || PRIORITY_CONFIG.medium;
                    const isEditing = editingId === memory.id;
                    
                    return (
                      <div
                        key={memory.id}
                        data-testid={`memory-item-${memory.id}`}
                        className={`p-4 rounded-lg border transition-all ${
                          isEditing 
                            ? 'bg-violet-900/20 border-violet-500' 
                            : 'bg-zinc-800 border-zinc-700 hover:border-zinc-600'
                        }`}
                      >
                        {isEditing ? (
                          <div className="space-y-3">
                            <textarea
                              value={editContent}
                              onChange={(e) => setEditContent(e.target.value)}
                              data-testid="edit-memory-content"
                              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-violet-500 resize-none"
                              rows={3}
                            />
                            <div className="flex gap-2">
                              <select
                                value={editCategory}
                                onChange={(e) => setEditCategory(e.target.value)}
                                data-testid="edit-category"
                                className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-600 rounded-lg text-sm text-white"
                              >
                                {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                                  <option key={key} value={key}>{config.label[language]}</option>
                                ))}
                              </select>
                              <select
                                value={editPriority}
                                onChange={(e) => setEditPriority(e.target.value)}
                                data-testid="edit-priority"
                                className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-600 rounded-lg text-sm text-white"
                              >
                                {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                                  <option key={key} value={key}>{config.label[language]}</option>
                                ))}
                              </select>
                            </div>
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={cancelEditing}
                                data-testid="btn-cancel-edit"
                                className="px-3 py-1.5 text-sm text-zinc-400 hover:text-white"
                              >
                                {language === 'es' ? 'Cancelar' : 'Cancel'}
                              </button>
                              <button
                                onClick={() => updateMemory(memory.id)}
                                data-testid="btn-save-edit"
                                className="flex items-center gap-1 px-3 py-1.5 bg-violet-600 hover:bg-violet-500 text-white text-sm rounded-lg"
                              >
                                <Check className="w-4 h-4" />
                                {language === 'es' ? 'Guardar' : 'Save'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-lg ${categoryConfig.bg}`}>
                                <CategoryIcon category={memory.category} size="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-zinc-200">{memory.content}</p>
                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                  <span className={`px-2 py-0.5 rounded-full text-xs ${categoryConfig.bg} ${categoryConfig.color}`}>
                                    {categoryConfig.label[language]}
                                  </span>
                                  <span className={`px-2 py-0.5 rounded-full text-xs ${priorityConfig.bg} ${priorityConfig.color}`}>
                                    {priorityConfig.label[language]}
                                  </span>
                                  <span className="text-xs text-zinc-500">
                                    {new Date(memory.created_at).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US')}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => startEditing(memory)}
                                  data-testid={`btn-edit-${memory.id}`}
                                  className="p-1.5 text-zinc-500 hover:text-violet-400 rounded transition-all"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setDeleteConfirm(memory.id)}
                                  data-testid={`btn-delete-${memory.id}`}
                                  className="p-1.5 text-zinc-500 hover:text-red-400 rounded transition-all"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            
                            {/* Delete Confirmation */}
                            {deleteConfirm === memory.id && (
                              <div className="mt-3 p-3 bg-red-900/20 border border-red-800 rounded-lg">
                                <p className="text-sm text-red-300 mb-2">
                                  {language === 'es' ? '¿Eliminar esta memoria?' : 'Delete this memory?'}
                                </p>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => setDeleteConfirm(null)}
                                    data-testid="btn-cancel-delete"
                                    className="px-3 py-1.5 text-sm text-zinc-400 hover:text-white"
                                  >
                                    {language === 'es' ? 'Cancelar' : 'Cancel'}
                                  </button>
                                  <button
                                    onClick={() => deleteMemory(memory.id)}
                                    data-testid="btn-confirm-delete"
                                    className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white text-sm rounded-lg"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    {language === 'es' ? 'Eliminar' : 'Delete'}
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Clear All Button */}
              {memories.length > 0 && (
                <div className="pt-4 border-t border-zinc-700">
                  {deleteConfirm === 'all' ? (
                    <div className="p-4 bg-red-900/20 border border-red-800 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-5 h-5 text-red-400" />
                        <p className="text-sm text-red-300 font-medium">
                          {language === 'es' ? '¿Eliminar TODAS las memorias?' : 'Delete ALL memories?'}
                        </p>
                      </div>
                      <p className="text-xs text-red-400/70 mb-3">
                        {language === 'es' 
                          ? 'Esta acción no se puede deshacer. Se eliminarán todas tus memorias guardadas.'
                          : 'This action cannot be undone. All your saved memories will be deleted.'}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          data-testid="btn-cancel-clear-all"
                          className="px-3 py-1.5 text-sm text-zinc-400 hover:text-white"
                        >
                          {language === 'es' ? 'Cancelar' : 'Cancel'}
                        </button>
                        <button
                          onClick={clearAllMemories}
                          data-testid="btn-confirm-clear-all"
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white text-sm rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                          {language === 'es' ? 'Sí, eliminar todo' : 'Yes, delete all'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm('all')}
                      data-testid="btn-clear-all"
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-red-400 hover:bg-red-900/20 border border-red-800/50 rounded-lg transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                      {language === 'es' ? 'Limpiar todas las memorias' : 'Clear all memories'}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MemoryProfileModal;
